import React, { useState, useCallback, useRef, useEffect } from 'react';
import { cn } from '../primitives';

// Click animation styles for activity buttons
const clickAnimationStyles = `
/* Fieldset: horizontal slide/replace effect */
@keyframes fieldset-slide-out {
  0% { transform: translateX(0) scale(1); opacity: 1; }
  50% { transform: translateX(-20px) scale(0.85); opacity: 0.3; }
  51% { transform: translateX(20px) scale(0.85); opacity: 0.3; }
  100% { transform: translateX(0) scale(1); opacity: 1; }
}
@keyframes fieldset-slide-forward {
  0% { transform: translateX(0) scale(1); }
  50% { transform: translateX(-8px) scale(1.05); }
  100% { transform: translateX(0) scale(1); }
}
.activity-button--fieldset.activity-button--clicking .activity-button__icon-wrapper {
  animation: fieldset-slide-out 200ms ease-in-out;
}
.activity-button--fieldset.activity-button--clicking .activity-button__secondary-icon {
  animation: fieldset-slide-forward 200ms ease-in-out;
}

/* Toggle: simple pop effect */
@keyframes toggle-pop {
  0% { transform: scale(1); }
  50% { transform: scale(0.88); }
  100% { transform: scale(1); }
}
.activity-button--toggle.activity-button--clicking .activity-button__icon-wrapper {
  animation: toggle-pop 150ms ease-out;
}
`;

// Inject styles once
if (typeof document !== 'undefined') {
  const styleId = 'activity-button-animations';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = clickAnimationStyles;
    document.head.appendChild(style);
  }
}

// Score color mapping based on score value (matching Rails implementation)
// These colors match the non-ReactJS score_colors.css gradient with blue=170 consistently
const SCORE_COLORS: Record<number, string> = {
  0: '#fff',          // white (no score)
  1: '#ff99aa',       // rgb(255, 153, 170) - score 0-10
  2: '#ffadaa',       // rgb(255, 173, 170) - score 11-20
  3: '#ffc2aa',       // rgb(255, 194, 170) - score 21-30
  4: '#ffd6aa',       // rgb(255, 214, 170) - score 31-40
  5: '#ffebaa',       // rgb(255, 235, 170) - score 41-50
  6: '#ebffaa',       // rgb(235, 255, 170) - score 51-60
  7: '#d6ffaa',       // rgb(214, 255, 170) - score 61-70
  8: '#c2ffaa',       // rgb(194, 255, 170) - score 71-80
  9: '#adffaa',       // rgb(173, 255, 170) - score 81-90
  10: '#99ffaa',      // rgb(153, 255, 170) - score 91-100
};

function getScoreColor(score: number | null | undefined): string {
  if (score === null || score === undefined) return SCORE_COLORS[0]!;
  // Scale from 0-100 to 1-10 (index 0 is reserved for null/undefined)
  const scaledScore = Math.round((score / 100) * 9) + 1;
  const clampedScore = Math.max(1, Math.min(10, scaledScore));
  return SCORE_COLORS[clampedScore] ?? SCORE_COLORS[0]!;
}

// SVG background for numeric fields with progress indicator
function NumericProgressSvg({ percentage }: { percentage: number }) {
  const clampedPercentage = Math.max(0, Math.min(100, percentage));
  const fillHeight = (clampedPercentage / 100) * 57;
  const yOffset = 57 - fillHeight;

  return (
    <svg
      className="activity-button__progress-svg"
      viewBox="0 0 57 57"
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        borderRadius: '50%',
        overflow: 'hidden',
      }}
    >
      <defs>
        <clipPath id={`circle-clip-${percentage}`}>
          <circle cx="28.5" cy="28.5" r="28.5" />
        </clipPath>
      </defs>
      <rect
        x="0"
        y={yOffset}
        width="57"
        height={fillHeight}
        fill="#90EE90"
        clipPath={`url(#circle-clip-${percentage})`}
      />
    </svg>
  );
}

export type ActivityButtonVariant = 'toggle' | 'fieldset' | 'numeric';

export interface ActivityButtonProps {
  /** Unique identifier for the field */
  id: string;
  /** Display name of the field */
  name: string;
  /** Icon URL or component */
  icon?: string | React.ReactNode;
  /** Button variant type */
  variant?: ActivityButtonVariant;
  /** Whether the activity is currently active */
  active?: boolean;
  /** Whether the activity was automatically triggered */
  automated?: boolean;
  /** Score value (0-10) for color indication */
  score?: number | null;
  /** Whether this field has an associated goal */
  hasGoal?: boolean;
  /** Whether the fieldset is incomplete (shows dashed border) */
  incomplete?: boolean;
  /** Secondary field icons for fieldsets */
  secondaryFields?: Array<{
    score?: number | null;
  }>;
  /** For numeric fields: current value */
  value?: number;
  /** For numeric fields: target/max value for percentage calculation */
  targetValue?: number;
  /** For numeric fields: formatted value with units */
  formattedValue?: string;
  /** Callback when clicked */
  onClick?: () => void;
  /** Callback for long press (used for popovers) */
  onLongPress?: () => void;
  /** Whether the button is disabled */
  disabled?: boolean;
  /** Custom class name */
  className?: string;
  /** Test ID for testing */
  testId?: string;
  /** Custom render function for the icon (React Native support) */
  renderIcon?: (props: { icon: string | React.ReactNode; size: number }) => React.ReactNode;
}

/**
 * ActivityButton - A button component for diary activities
 *
 * Supports three variants:
 * - toggle: Simple on/off toggle for single activities
 * - fieldset: Group of related fields with secondary icons
 * - numeric: Numeric field with progress indicator
 */
export function ActivityButton({
  id,
  name,
  icon,
  variant = 'toggle',
  active = false,
  automated = false,
  score,
  hasGoal = false,
  incomplete = false,
  secondaryFields = [],
  value,
  targetValue,
  formattedValue,
  onClick,
  onLongPress,
  disabled = false,
  className,
  testId,
  renderIcon,
}: ActivityButtonProps) {
  const [longPressTimer, setLongPressTimer] = useState<ReturnType<typeof setTimeout> | null>(null);
  const [isPressed, setIsPressed] = useState(false);
  const [isClicking, setIsClicking] = useState(false);
  const clickAnimationRef = useRef<number | null>(null);

  // Cleanup animation timeout on unmount
  useEffect(() => {
    return () => {
      if (clickAnimationRef.current) {
        clearTimeout(clickAnimationRef.current);
      }
    };
  }, []);

  const handleTouchStart = useCallback(() => {
    if (onLongPress) {
      const timer = setTimeout(() => {
        onLongPress();
        setIsPressed(false);
      }, 500);
      setLongPressTimer(timer);
    }
    setIsPressed(true);
  }, [onLongPress]);

  const handleTouchEnd = useCallback(() => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
    setIsPressed(false);
  }, [longPressTimer]);

  const handleClick = useCallback(() => {
    if (!disabled && onClick) {
      // Trigger click animation for fieldsets and toggles
      if (variant === 'fieldset' || variant === 'toggle') {
        // Cancel any pending animation reset
        if (clickAnimationRef.current) {
          clearTimeout(clickAnimationRef.current);
        }
        // Animation duration: fieldset=200ms, toggle=150ms
        const duration = variant === 'fieldset' ? 200 : 150;
        // Reset and re-trigger animation
        setIsClicking(false);
        requestAnimationFrame(() => {
          setIsClicking(true);
          clickAnimationRef.current = window.setTimeout(() => {
            setIsClicking(false);
          }, duration);
        });
      }
      onClick();
    }
  }, [disabled, onClick, variant]);

  // Calculate numeric percentage
  const numericPercentage = variant === 'numeric' && targetValue && targetValue > 0
    ? Math.round((value || 0) / targetValue * 100)
    : 0;

  // Determine background color
  const backgroundColor = variant === 'numeric'
    ? '#fff' // Numeric uses SVG background
    : active
      ? getScoreColor(score)
      : '#fff'; // When not active, show white background

  // Render icon
  const renderIconContent = () => {
    if (renderIcon && icon) {
      return renderIcon({ icon, size: 35 });
    }

    if (typeof icon === 'string') {
      return (
        <img
          src={icon}
          alt=""
          style={{ width: 35, height: 35 }}
          className="activity-button__icon-img"
        />
      );
    }

    return icon;
  };

  // All buttons use the same width for proper alignment
  const containerWidth = 71;

  return (
    <div
      className={cn(
        'activity-button',
        `activity-button--${variant}`,
        active && 'activity-button--active',
        disabled && 'activity-button--disabled',
        isPressed && 'activity-button--pressed',
        isClicking && 'activity-button--clicking',
        className
      )}
      style={{
        display: 'inline-block',
        width: containerWidth,
        height: 90,
        marginBottom: 10,
        verticalAlign: 'top',
        position: 'relative',
      }}
      data-test-id={testId || `activity-button-${id}`}
    >
      {/* Button wrapper */}
      <button
        type="button"
        onClick={handleClick}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleTouchStart}
        onMouseUp={handleTouchEnd}
        onMouseLeave={handleTouchEnd}
        disabled={disabled}
        className="activity-button__trigger"
        style={{
          display: 'block',
          width: '100%',
          height: '100%',
          border: 'none',
          background: 'transparent',
          padding: 0,
          cursor: disabled ? 'not-allowed' : 'pointer',
          textAlign: 'center',
        }}
      >
        {/* Button content wrapper */}
        <div
          className="activity-button__wrapper"
          style={{
            display: 'inline-block',
            textAlign: 'center',
            width: '100%',
            height: '100%',
          }}
        >
          {/* Icon wrapper with secondary fields */}
          <div style={{ position: 'relative', display: 'inline-block' }}>
            {/* Secondary field icons (for fieldsets) */}
            {variant === 'fieldset' && secondaryFields.slice(0, 2).map((field, index) => (
              <div
                key={index}
                className="activity-button__secondary-icon"
                style={{
                  position: 'absolute',
                  left: 10 + index * 4,
                  top: 1,
                  zIndex: 9 - index,
                  width: 57,
                  height: 57,
                  borderRadius: '50%',
                  border: '2px solid rgba(0, 0, 0, 0.15)',
                  backgroundColor: getScoreColor(field.score),
                  boxSizing: 'border-box',
                }}
              />
            ))}

            {/* Main icon wrapper */}
            <div
              className={cn(
                'activity-button__icon-wrapper',
                incomplete && 'activity-button__icon-wrapper--incomplete'
              )}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 57,
                height: 57,
                borderRadius: '50%',
                border: incomplete ? '2px dashed #DDD' : (variant === 'fieldset' ? '2px solid rgba(0, 0, 0, 0.15)' : '1px solid #DDD'),
                backgroundColor,
                position: 'relative',
                zIndex: 10,
                overflow: 'hidden',
                boxSizing: 'border-box',
              }}
            >
              {/* Numeric progress SVG background */}
              {variant === 'numeric' && (
                <NumericProgressSvg percentage={numericPercentage} />
              )}

              {/* Icon */}
              <div style={{ position: 'relative', zIndex: 1 }}>
                {renderIconContent()}
              </div>
            </div>

            {/* Automation flag - positioned outside icon wrapper */}
            {automated && (
              <span
                className="activity-button__flag"
                style={{
                  position: 'absolute',
                  right: -4,
                  bottom: 0,
                  zIndex: 20,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 18,
                  height: 18,
                  borderRadius: '50%',
                  backgroundColor: '#fff',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                }}
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="#f59e0b"
                  style={{ display: 'block' }}
                >
                  <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
                </svg>
              </span>
            )}

            {/* Goal badge - positioned outside icon wrapper */}
            {hasGoal && (
              <span
                className="activity-button__goal"
                style={{
                  position: 'absolute',
                  right: -2,
                  top: -2,
                  color: '#0d6efd',
                  fontSize: 16,
                  zIndex: 20,
                  textShadow: '0 0 3px white, 0 0 3px white',
                }}
              >
                🎯
              </span>
            )}
          </div>

          {/* Text label */}
          {name && (
            <div
              className="activity-button__text"
              style={{
                display: 'block',
                width: '100%',
                fontSize: 10,
                lineHeight: 1.2,
                marginTop: 2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {variant === 'numeric' && formattedValue ? (
                <>
                  {name}
                  <br />
                  <em style={{ fontStyle: 'italic', color: '#666' }}>{formattedValue}</em>
                </>
              ) : (
                name
              )}
            </div>
          )}
        </div>
      </button>
    </div>
  );
}

export default ActivityButton;
